/**
 * The feedback panel shown once a question is terminal (brief §practice:
 * "feedback panel (correct/partial/incorrect, per-part expected values,
 * near-miss message, full solution with solutionFigure, option
 * explanations)"). After a wrong or partial MC/numeric answer the worked
 * solution is folded behind "Visa lösningen" with a reflection prompt above it
 * (learning-evidence §4.2(2)); the parent keys this panel by question id.
 *
 * Guided retry (pedagogy spec step 2.4b): a wrong first try is withheld and answered by
 * `RetryPanel`; this panel is the terminal state after it. `result.score` stays the first try's, so
 * a retried item (`retried`) gets the retry's headline ("Rätt i andra försöket") without a points
 * badge and keeps the solution folded; after "Jag kör fast" (`solutionOpen`) the first try's
 * verdict stays and the solution is open. A terminal answer words its near miss in full (`sign`,
 * `factor` and the older kinds, through `DiagnosisMessage`), an authored diagnosis wins over it.
 *
 * Contrast follow-up (step 2.6): a twin answered after a prediction opens with `PredictionVerdict`,
 * the prediction's verdict and its authored reason; it arrives with the terminal response only.
 *
 * Confidence callback (pedagogy review A4): the rating from `ConfidenceControl`
 * is asked before every check but never shown back at feedback time — a
 * one-line note closes that loop for a graded answer (auto- or self-graded):
 * confident-but-wrong points at the solution, correct-but-unsure asks what
 * caused the hesitation. Silent otherwise (a give-up has no `correct`).
 */
import { CheckCircle2, Circle, XCircle } from "lucide-react";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import type { PublicQuestion } from "@shared/types";
import { Figure } from "@client/components/Figure";
import { Localized } from "@client/components/Localized";
import { SourceBadge } from "@client/components/SourceBadge";
import { Badge, Button, Card } from "@client/components/ui";
import { useLang, useT } from "@client/i18n";
import { formatPoints, formatScientific } from "@client/lib/format";
import { formatUnit } from "@client/lib/units";
import { DiagnosisMessage } from "./DiagnosisMessage";
import { PredictionVerdict } from "./PredictionVerdict";
import { SolutionShownContext } from "./SelfExplanation";
import type { QuestionResult } from "./session";
import { TopicChip } from "./TopicChip";

export interface FeedbackPanelProps {
  result: QuestionResult;
  /** The question that was answered: its `source` for the provenance badge, its topic for "Läs mer". */
  question?: PublicQuestion;
  /**
   * The optional self-explanation field (`SelfExplanation.tsx`), shown above the folded solution.
   * It is told when the solution is opened (`SolutionShownContext`).
   */
  selfExplanation?: ReactNode;
}

function outcomeTone(result: QuestionResult): "success" | "warning" | "danger" {
  if (result.gaveUp) return "danger";
  const score = result.score ?? 0;
  const max = result.maxScore ?? 0;
  if (max > 0 && score >= max) return "success";
  if (score > 0) return "warning";
  return "danger";
}

export function FeedbackPanel({ result, question, selfExplanation }: FeedbackPanelProps) {
  const t = useT();
  const { lang } = useLang();
  const [solutionShown, setSolutionShown] = useState(false);
  const solutionHeadingRef = useRef<HTMLHeadingElement>(null);
  const headlineRef = useRef<HTMLSpanElement>(null);
  // Review-A fix round (batch A fix 1): links the confidence note to the
  // focused outcome span, so a screen reader reads "Fel — Du var säker, men
  // svaret blev fel…" together the instant focus lands, rather than the
  // note's own `role="status"` racing (or being dropped by) the focus move
  // in the same tick.
  const confidenceNoteId = useId();
  useEffect(() => {
    if (solutionShown) solutionHeadingRef.current?.focus();
  }, [solutionShown]);
  // Once per mount (the parent keys this panel by question id, so a fresh
  // mount means a fresh grading result to announce) — not on every
  // `solutionShown` toggle.
  useEffect(() => {
    headlineRef.current?.focus();
  }, []);
  const revealed = result.revealed;
  if (!revealed) return null;

  // Open answers saw the solution at "Klar"; a give-up exists to read it; "Jag kör fast" opens it
  // at once. `score` is the first try's (a retry leaves it alone), so a retried item stays folded.
  const folded =
    !solutionShown &&
    revealed.type !== "open" &&
    !result.gaveUp &&
    !result.solutionOpen &&
    (result.score ?? 0) < (result.maxScore ?? 0);

  // The first try's tone drives the confidence note; after a retry the headline is the retry's.
  const firstTone = outcomeTone(result);
  const retried = result.retried;
  const tone = retried
    ? retried.correct
      ? "success"
      : retried.score > 0
        ? "warning"
        : "danger"
    : firstTone;
  const Icon = tone === "success" ? CheckCircle2 : tone === "warning" ? Circle : XCircle;
  const headline = result.gaveUp
    ? t("practice.feedback.gaveUp")
    : retried
      ? retried.correct
        ? t("practice.feedback.correctOnRetry")
        : t("practice.feedback.incorrectAfterRetry")
      : tone === "success"
        ? t("practice.feedback.correct")
        : tone === "warning"
          ? t("practice.feedback.partial")
          : t("practice.feedback.incorrect");

  // Pedagogy review A4 (review-W fix, Important #1): shown only for a graded
  // answer (auto- or self-graded — a give-up never sets `correct`), and keyed
  // on the same tone the headline uses rather than raw `correct` — a partial
  // score ("Delvis rätt", tone "warning") never gets a confidence line, since
  // it would otherwise contradict the headline on self-graded open answers
  // (correct at >=50%) and multi-part numeric answers (correct only if every
  // part is right).
  const confidenceNote =
    result.confidence === undefined || result.correct === undefined
      ? null
      : firstTone === "success" && result.confidence <= 2
        ? t("practice.confidence.feedback.rightUnsure")
        : firstTone === "danger" && result.confidence >= 3
          ? t("practice.confidence.feedback.wrongSure")
          : null;

  return (
    <Card
      className="mt-4"
      title={
        // UX-03: the panel mounts fresh (keyed by question id) the moment
        // the answer is graded, and `AnswerArea` unmounts the button that
        // had focus in the same instant — without this, focus falls to
        // `<body>` and the outcome is never spoken. Focus lands on this
        // span, inside the heading `Card` renders from `title`. A
        // `role="status"` here (tried first) would work in jsdom, but a
        // real browser then reports the *heading's own* accessible name as
        // empty — nesting a live-region role inside it blanks it out —
        // silently breaking every "Fel"/"Rätt!" heading query. Caught only
        // by the real Chromium e2e run, not the unit tests; a plain
        // focusable span avoids the trap while still moving focus onto the
        // outcome text and announcing it.
        <span
          ref={headlineRef}
          tabIndex={-1}
          aria-describedby={confidenceNote ? confidenceNoteId : undefined}
          className="inline-flex items-center gap-2 outline-none"
        >
          <Icon
            size={20}
            aria-hidden="true"
            className={
              tone === "success"
                ? "text-green-600 dark:text-green-400"
                : tone === "warning"
                  ? "text-amber-600 dark:text-amber-400"
                  : "text-red-600 dark:text-red-400"
            }
          />
          {headline}
        </span>
      }
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <SourceBadge source={question?.source} />
          {/* After a retry the points are the first try's: a badge beside "Rätt i andra försöket"
              would contradict it, so the retry shows none. */}
          {!result.gaveUp && !retried && result.maxScore !== undefined && (
            <Badge tone={tone}>
              {formatPoints(result.score ?? 0, lang)} / {formatPoints(result.maxScore, lang)}
            </Badge>
          )}
        </div>
      }
    >
      {/* A contrast twin's prediction, made before it: right or not, and why (step 2.6). */}
      <PredictionVerdict result={result} />

      {confidenceNote && (
        <p
          id={confidenceNoteId}
          role="status"
          className="mb-4 text-sm font-medium text-ink-800 dark:text-ink-100"
        >
          {confidenceNote}
        </p>
      )}

      {revealed.type === "numeric" && result.parts && result.parts.length > 0 && (
        <ul className="mb-4 space-y-1 text-sm">
          {result.parts.map((part) => {
            const multiPart = result.parts!.length > 1;
            return (
              <li key={part.id} className="flex flex-wrap items-baseline gap-2">
                {part.correct ? (
                  <CheckCircle2
                    size={14}
                    className="shrink-0 text-green-600 dark:text-green-400"
                    aria-hidden="true"
                  />
                ) : (
                  <XCircle
                    size={14}
                    className="shrink-0 text-red-600 dark:text-red-400"
                    aria-hidden="true"
                  />
                )}
                {/* UX-04: right/wrong was conveyed only by the icon above,
                    which is aria-hidden, and a multi-part row named neither
                    part — screen readers heard "Ditt svar: 2 · Rätt svar:
                    1,431" twice with no way to tell (a) from (b). The visible
                    "(a)" comes before the sr-only right/wrong text in reading
                    order, not after, so a screen reader says "(a), Rätt
                    delsvar, Ditt svar…" rather than naming the outcome before
                    the part it belongs to. A single-part row has no "(a)" to
                    read first and says plain "Rätt"/"Fel", not "delsvar"
                    (sub-answer) — there is only the one answer. */}
                {multiPart && <strong>({part.id}) </strong>}
                <span className="sr-only">
                  {multiPart
                    ? part.correct
                      ? t("practice.feedback.partCorrect")
                      : t("practice.feedback.partIncorrect")
                    : part.correct
                      ? t("practice.feedback.partCorrectSingle")
                      : t("practice.feedback.partIncorrectSingle")}
                </span>
                <span>
                  {t("practice.feedback.yourAnswer")}{" "}
                  {/* "Jag kör fast" skips the grading: no parsed value, so the text as typed. */}
                  {part.parsedValue === null
                    ? (part.typed ?? "—")
                    : formatScientific(part.parsedValue, lang)}
                  {" · "}
                  {t("practice.feedback.expected")} {formatScientific(part.expected, lang)}{" "}
                  {formatUnit(part.unit)}
                </span>
                {/* The authored diagnosis wins; else the near miss, in full on a terminal answer. */}
                <DiagnosisMessage
                  diagnosis={part.diagnosis}
                  nearMiss={part.nearMiss}
                  unit={part.unit}
                  className="text-amber-700 dark:text-amber-300"
                />
              </li>
            );
          })}
        </ul>
      )}

      {revealed.type === "mc" && (
        <ul className="mb-4 space-y-2 text-sm">
          {revealed.options.map((option) => {
            const picked = result.selectedOptionIds?.includes(option.id) ?? false;
            // After a retry the first pick is told apart from the final one.
            const firstPick =
              !picked && (result.firstTry?.mcSelected?.includes(option.id) ?? false);
            const diagnosis =
              result.mcDiagnosis?.optionId === option.id ? result.mcDiagnosis.message : undefined;
            return (
              <li
                key={option.id}
                className={
                  option.correct
                    ? "rounded-lg border border-green-300 bg-green-50 p-2 dark:border-green-800 dark:bg-green-950/50"
                    : "rounded-lg border border-ink-200 p-2 dark:border-ink-800"
                }
              >
                <div className="flex flex-wrap items-center gap-2 font-medium text-ink-800 dark:text-ink-100">
                  {option.correct ? (
                    <CheckCircle2
                      size={14}
                      className="shrink-0 text-green-600 dark:text-green-400"
                      aria-hidden="true"
                    />
                  ) : null}
                  <Localized value={option.text} variant="inline" />
                  {/* UX-04: only the correct option was marked, and only with
                      a hidden icon — the student's own pick was not shown at
                      all. Text badges carry both, for every reader. */}
                  {picked && (
                    <Badge tone={option.correct ? "success" : "danger"}>
                      {t("practice.feedback.optionYourChoice")}
                    </Badge>
                  )}
                  {firstPick && <Badge tone="neutral">{t("practice.retry.firstChoice")}</Badge>}
                  {option.correct && (
                    <Badge tone="success">{t("practice.feedback.optionCorrect")}</Badge>
                  )}
                </div>
                {diagnosis && (
                  <div className="mt-1 font-normal text-amber-800 dark:text-amber-300">
                    <Localized value={diagnosis} variant="compact" />
                  </div>
                )}
                {option.explanation && (
                  <div className="mt-1 text-ink-600 dark:text-ink-400">
                    <Localized value={option.explanation} variant="compact" />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {revealed.type === "mc" && result.justification && (
        <div className="mb-4 rounded-lg border border-ink-200 p-3 text-sm dark:border-ink-800">
          <h3 className="mb-1 font-medium text-ink-700 dark:text-ink-200">
            {t("practice.feedback.yourJustification")}
          </h3>
          <p className="whitespace-pre-wrap break-words text-ink-800 dark:text-ink-100">
            {result.justification}
          </p>
        </div>
      )}

      {revealed.type === "open" && (
        <div className="mb-4 space-y-1 text-sm">
          {revealed.rubric.map((item) => (
            <div key={item.id} className="flex items-start gap-2">
              {result.ticked?.includes(item.id) ? (
                <CheckCircle2
                  size={14}
                  className="mt-0.5 shrink-0 text-green-600 dark:text-green-400"
                  aria-hidden="true"
                />
              ) : (
                <Circle
                  size={14}
                  className="mt-0.5 shrink-0 text-ink-400 dark:text-ink-600"
                  aria-hidden="true"
                />
              )}
              <span>
                <Localized value={item.text} variant="inline" /> ({formatPoints(item.points, lang)})
              </span>
            </div>
          ))}
          {revealed.finalAnswer && (
            <div className="mt-2 font-medium text-ink-800 dark:text-ink-100">
              <Localized value={revealed.finalAnswer} variant="inline" />
            </div>
          )}
        </div>
      )}

      {/* Saved on "Visa lösningen" or "Nästa", whichever comes first (spec §5.3.6). */}
      <SolutionShownContext.Provider value={solutionShown}>
        {selfExplanation}
      </SolutionShownContext.Provider>

      <div className="border-t border-ink-200 pt-4 dark:border-ink-800">
        {folded ? (
          <>
            <p className="mb-3 text-sm text-ink-700 dark:text-ink-200">
              {t("practice.feedback.reflectPrompt")}
            </p>
            <Button variant="secondary" onClick={() => setSolutionShown(true)}>
              {t("practice.feedback.showSolution")}
            </Button>
          </>
        ) : (
          <>
            <h3
              ref={solutionHeadingRef}
              tabIndex={-1}
              className="mb-2 text-sm font-medium text-ink-700 outline-none dark:text-ink-200"
            >
              {t("practice.feedback.solution")}
            </h3>
            <Localized value={revealed.solution} />
            {revealed.solutionFigure && <Figure figure={revealed.solutionFigure} />}
            {/* P1-4: where to read more, once the topic is no longer a hint. */}
            {question && <TopicChip topicId={question.topic} variant="readMore" />}
          </>
        )}
      </div>
    </Card>
  );
}
